import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, UsersIcon, ClockIcon, BadgeCheckIcon, BuildingIcon, ScaleIcon } from 'lucide-react';
export function WhatWeDo() {
  const credentials = [{
    icon: BadgeCheckIcon,
    label: 'Bonded & Certified'
  }, {
    icon: BuildingIcon,
    label: 'Authorized by CJIS'
  }, {
    icon: ScaleIcon,
    label: 'State of Maryland Approved'
  }];
  const values = [{
    icon: ShieldCheckIcon,
    title: 'Official Live Scan',
    description: 'As an official Live Scan provider, we offer the only fingerprinting method accepted by the State of Maryland for background checks.'
  }, {
    icon: UsersIcon,
    title: 'Certified Technicians',
    description: 'Our highly trained and certified fingerprint technicians ensure your fingerprinting process is smooth, quick, and accurate.'
  }, {
    icon: ClockIcon,
    title: 'Quick & Hassle-Free',
    description: 'We understand the value of your time and strive to make the fingerprinting process as quick and hassle-free as possible.'
  }];
  return <section id="about" className="py-12 lg:py-20 bg-slate-50 overflow-hidden border-b border-slate-200/60" aria-labelledby="about-heading" data-id="element-257">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-258">
        {/* About Us - Two Column Layout */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-12 lg:mb-24" data-id="element-259">
          {/* Left - Image */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} className="lg:col-span-5 relative" data-id="element-260">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200 border-8 border-white" data-id="element-261">
              <img src="https://cdn.magicpatterns.com/uploads/51ztD64aFZM9MBEzkJVWmz/ChatGPT_Image_Apr_9,_2026,_12_26_48_AM.png" alt="Digital biometric fingerprint scan with holographic security interface" className="w-full h-auto object-cover aspect-[4/3]" data-id="element-262" />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" data-id="element-263" />
            </div>

            {/* Floating credential badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="absolute -bottom-12 -right-4 sm:right-12 bg-white rounded-2xl shadow-2xl shadow-slate-200 border border-slate-100 px-8 py-6 flex items-center gap-6" data-id="element-264">
              <div className="bg-[#4AA6CF]/10 p-4 rounded-xl" data-id="element-265">
                <ShieldCheckIcon className="h-8 w-8 text-[#4AA6CF]" aria-hidden="true" data-id="element-266" />
              </div>
              <div data-id="element-267">
                <p className="text-4xl font-extrabold text-slate-900" data-id="element-268">10+</p>
                <p className="text-xs text-slate-400 font-extrabold uppercase tracking-widest" data-id="element-269">
                  Years Experience
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} className="lg:col-span-7" data-id="element-270">
            <span className="inline-block text-[#2B7A9E] font-bold text-sm tracking-widest uppercase mb-4" data-id="element-271">
              About Us
            </span>
            <h2 id="about-heading" className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-8 leading-tight tracking-tight" data-id="element-272">
              Who We Are
            </h2>
            <hr className="border-t-4 border-[#4AA6CF] w-24 rounded-full mb-12" aria-hidden="true" data-id="element-273" />

            <div className="space-y-6 text-slate-600 leading-relaxed text-lg" data-id="element-274">
              <p data-id="element-275">
                Ambient Solutions, LLC, operating under the trade name Ambient
                Fingerprinting Solution, is a premier provider of secure
                electronic fingerprinting services in Columbia, Maryland. What
                sets us apart is our{' '}
                <strong className="text-slate-900 font-bold" data-id="element-276">
                  mobile fingerprinting service
                </strong>{' '}
                — we travel directly to your office or facility, eliminating the
                need for your team to leave the workplace.
              </p>
              <p data-id="element-277">
                We are{' '}
                <strong className="text-[#1E1F55]" data-id="element-278">
                  Bonded, Certified, and Authorized by CJIS
                </strong>{' '}
                (Criminal Justice Information Systems) to operate under COMAR
                Regulation 12.15.05 and the Criminal Procedure Article §10-221,
                Annotated Code of Maryland. We specialize in{' '}
                <strong className="text-[#1E1F55]" data-id="element-279">
                  Live Scan Fingerprinting
                </strong>
                , which is the exclusive method accepted by the State of
                Maryland for official background checks.
              </p>
              <p data-id="element-280">
                All services are{' '}
                <strong className="text-[#1E1F55]" data-id="element-281">by appointment only</strong>{' '}
                — we do not accept walk-ins. We already partner with multiple
                companies across Maryland for group fingerprinting (5 or more
                employees) and are actively expanding our corporate client base.
              </p>
            </div>

            {/* Credential Badges */}
            <div className="flex flex-wrap gap-3 mt-8" data-id="element-282">
              {credentials.map((cred, index) => <div key={index} className="flex items-center gap-3 px-6 py-3 bg-white rounded-full border border-slate-100 shadow-sm" data-id="element-283">
                  <cred.icon className="h-4 w-4 text-[#4AA6CF]" aria-hidden="true" data-id="element-284" />
                  <span className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-285">
                    {cred.label}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Value Cards */}
        <div className="text-center mt-24 mb-16 lg:mb-20" data-id="element-286">
          <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 tracking-tight" data-id="element-287">
            What Sets Us Apart
          </h3>
          <div className="w-20 h-1.5 bg-[#4AA6CF] mx-auto rounded-full" aria-hidden="true" data-id="element-288"></div>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.2
          }
        }
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-289">
          {values.map((card, index) => <motion.div key={index} variants={{
          hidden: {
            opacity: 0,
            y: 20
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: 'easeOut'
            }
          }
        }} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 group" data-id="element-290">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-slate-900 transition-all duration-500 shadow-inner border border-slate-100" data-id="element-291">
                <card.icon className="h-8 w-8 text-[#4AA6CF] group-hover:text-white transition-colors duration-500" aria-hidden="true" data-id="element-292" />
              </div>
              <h3 className="text-xl font-extrabold text-slate-900 mb-4 tracking-tight" data-id="element-293">
                {card.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-base" data-id="element-294">
                {card.description}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}